import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { useA11y } from "../../context/A11yContext";
import { useAuth } from "../../context/AuthContext";
import { btn } from "../kit";

function Radio({ name, legend, value, options, onChange }) {
  return (
    <fieldset className="space-y-2">
      <legend className="font-semibold text-ink mb-2">{legend}</legend>
      <div className="grid grid-cols-3 gap-2">
        {options.map(([v, l]) => (
          <label key={v} className={`flex min-h-[48px] cursor-pointer items-center justify-center rounded-lg border-2 px-2 text-center font-medium ${value === v ? "border-blue bg-mist text-navy" : "border-line"}`}>
            <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} className="sr-only" data-testid={`a11y-${name}-${v}`} />
            {l}{value === v && <span className="sr-only"> (selected)</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Toggle({ id, label, hint, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-line p-3">
      <div><p id={`${id}-label`} className="font-semibold">{label}</p><p id={`${id}-hint`} className="text-sm text-sub">{hint}</p></div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`w-7 text-sm font-semibold ${checked ? "text-link" : "text-sub"}`} aria-hidden="true">{checked ? "On" : "Off"}</span>
        <button type="button" role="switch" aria-checked={checked} aria-labelledby={`${id}-label`} aria-describedby={`${id}-hint`} onClick={() => onChange(!checked)} data-testid={`a11y-${id}-switch`}
          className={`relative h-8 w-14 rounded-full border-2 transition-colors ${checked ? "bg-blue border-blue" : "bg-white border-field"}`}>
          <span className={`absolute left-0.5 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full transition-transform ${checked ? "translate-x-6 bg-white" : "translate-x-0 bg-field"}`} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default function A11yPanel() {
  const { prefs, update, reset, open, setOpen } = useA11y();
  const { user } = useAuth();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl bg-white" data-testid="accessibility-panel">
        <DialogHeader>
          <DialogTitle className="text-2xl text-navy">Accessibility settings</DialogTitle>
          <DialogDescription className="text-sub">{user ? "Saved to your account and applied on every device you sign in on." : "Saved in this browser. Sign in to keep them on your account."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <Radio name="textSize" legend="Text size" value={prefs.textSize} onChange={(v) => update("textSize", v)} options={[["normal", "Default"], ["large", "Large"], ["xlarge", "Extra large"]]} />
          <Radio name="lineSpacing" legend="Line spacing" value={prefs.lineSpacing} onChange={(v) => update("lineSpacing", v)} options={[["normal", "Default"], ["relaxed", "Relaxed"], ["wide", "Extra wide"]]} />
          <Toggle id="contrast" label="Increase contrast" hint="Black text on white with stronger borders." checked={!!prefs.contrast} onChange={(v) => update("contrast", v)} />
          <Toggle id="motion" label="Reduce motion" hint="Turns off transitions and animations." checked={!!prefs.reduceMotion} onChange={(v) => update("reduceMotion", v)} />
          <Toggle id="simple" label="Simplified reading layout" hint="Single column, fewer decorative elements." checked={!!prefs.simple} onChange={(v) => update("simple", v)} />
          <section aria-labelledby="kbd-h" className="rounded-lg bg-surface p-4">
            <h3 id="kbd-h" className="font-semibold text-ink">Keyboard shortcuts</h3>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt><kbd className="font-mono font-semibold">Alt + Shift + A</kbd></dt><dd>Open or close these settings</dd>
              <dt><kbd className="font-mono font-semibold">Alt + Shift + S</kbd></dt><dd>Jump to the search field on the page</dd>
              <dt><kbd className="font-mono font-semibold">Alt + Shift + M</kbd></dt><dd>Jump to main content</dd>
              <dt><kbd className="font-mono font-semibold">Tab / Shift + Tab</kbd></dt><dd>Move between controls</dd>
              <dt><kbd className="font-mono font-semibold">Esc</kbd></dt><dd>Close dialogs and menus</dd>
            </dl>
          </section>
          <div className="flex flex-wrap justify-between gap-3">
            <button className={btn("secondary")} onClick={reset} data-testid="a11y-reset-btn">Reset to defaults</button>
            <Link to="/accessibility" className={btn("ghost")} onClick={() => setOpen(false)} data-testid="a11y-statement-link">Accessibility statement</Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
